import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { FiskalyService } from '../../core/services/FiskalyService';
import { ITseDetails } from '../../core/interfaces/ITseDetails';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { finalize, interval } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { CopyTableDetailsDirective } from '../../../../shared/directives/copy-table-details.directive';
import { TseExportStates } from '../../core/enums/TseExportStates';

@Component({
  selector: 'app-terminal-tse-details-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    LoaderComponent,
    CopyTableDetailsDirective
  ],
  templateUrl: './terminal-tse-details-modal.component.html',
  styleUrl: './terminal-tse-details-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TerminalTseDetailsModalComponent extends BaseModalComponent {

  @ViewChild('tseDetailsTable') tseDetailsTable: ElementRef<HTMLTableElement>;

  public tseDetails: ITseDetails;
  public generatedFile: Blob;
  public readonly isLoading = signal(false);
  public readonly isExportGenerating = signal(false);

  private readonly _fiskalyService = inject(FiskalyService);

  public override open(val?: null): void {
    this.generatedFile = null;
    super.open(val);
    this.isLoading.set(true);
    this.isExportGenerating.set(false);

    this._fiskalyService.getTerminalTseDetails()
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: res => {
          this.tseDetails = res.data;
          this.cdr.detectChanges();
          setTimeout(() => this.cdr.detectChanges(), 100);
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`tse-details/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public exportTransactions(): void {
    this.isExportGenerating.set(true);

    this._fiskalyService.triggerTseExport()
      .subscribe({
        next: res => {
          const exportId = res.data.id;

          this.notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'Export is generating, please wait.'.trans(`download-tse-export/notifications/exportIsGenerating.Message`)
          );

          const checkExportStatusIntervalSub = interval(1000 * 10).subscribe(() => {
            this._fiskalyService.checkExportStatus(exportId)
              .subscribe({
                next: res => {
                  if (res.data.state === TseExportStates.COMPLETED) {
                    checkExportStatusIntervalSub.unsubscribe();

                    this._fiskalyService.downloadTseExport(exportId)
                      .pipe(
                        finalize(() => this.isExportGenerating.set(false))
                      )
                      .subscribe({
                        next: res => {
                          this.generatedFile = res;

                          this.notificationService.pushSuccess(
                            'Erfolg'.trans('notifications/success.Title'),
                            'Export is ready to download.'.trans(`download-tse-export/notifications/exportIsReadyToDownload.Message`)
                          );
                        },
                        error: ({ error }: ApiErrorResponse) => {
                          this.notificationService.pushError(
                            'Fehler'.trans('notifications/error.Title'),
                            error.message.trans(`download-tse-export/notifications/${error.message}.Message`)
                          );
                        }
                      })
                  }
                },
                error: ({ error }: ApiErrorResponse) => {
                  this.isExportGenerating.set(false);
                  checkExportStatusIntervalSub.unsubscribe();

                  this.notificationService.pushError(
                    'Fehler'.trans('notifications/error.Title'),
                    error.message.trans(`check-export-status/notifications/${error.message}.Message`)
                  );
                }
              });
          });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.isExportGenerating.set(false);

          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`trigger-tse-export/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public openFile(): void {
    open(URL.createObjectURL(this.generatedFile), '_blank');
  }
}
